<template>
  <div class="filter-search-product-grid product-grid container">
    <div
      v-if="pagination.hasPreviousPage"
      class="product-grid__pagination-prev-wrapper grid"
    >
      <div class="col xs2-5 l6-7">
        <btn
          class="product-grid__load-button product-grid__load-button--prev"
          type="button"
          :disabled="(loading || !pagination.hasPreviousPage) || null"
          :url="loadPreviousLink"
          :show-state-icon="loading"
          :label="$string('general.pagination.load_previous')"
          @click.prevent="$emit('load-previous')"
        >
          <template #stateIcon>
            <icon-loading />
          </template>
        </btn>
      </div>
    </div>

    <template v-if="items.length === 0">
      <slot name="noResults">
        <div class="product-grid__no-results">
          <p
            class="text-body-s-regular"
            v-text="$string('collection.no_results')"
          />
        </div>
      </slot>
    </template>

    <ul class="product-grid__grid grid">
      <template
        v-for="(item, index) of items"
        :key="`product-${item.handle}-${index}`"
      >
        <li
          v-if="showInGridBanner && index === 4"
          class="product-grid__item col"
        >
          <in-grid-banner :banner="inGridBanner" />
        </li>

        <li class="product-grid__item col">
          <product-card
            :ref="setItemRefs"
            :class="{
              'product-card--small': mobileColumnCount === 2
            }"
            :product="item"
            :lazyload="index > 4"
            :one-per-order-enabled="item.limits?.onePerOrder"
            :loaded-animation="index > 4"
          />
        </li>
      </template>

      <li
        v-if="showInGridBanner && items.length < 5"
        class="product-grid__item col"
      >
        <in-grid-banner :banner="inGridBanner" />
      </li>
    </ul>

    <div
      v-show="items.length > 0"
      class="product-grid__pagination-next-wrapper grid"
    >
      <div class="product-grid__pagination col xs2-5 l6-7">
        <p
          class="text-body-s-regular"
          v-text="countMessage"
        />

        <btn
          v-show="pagination.hasNextPage"
          class="product-grid__load-button product-grid__load-button--next"
          type="button"
          :disabled="(loading || !pagination.hasNextPage) || null"
          :label="$string('general.pagination.load_more')"
          :show-state-icon="loading"
          :url="loadMoreLink"
          @click.prevent="$emit('load-more')"
        >
          <template #stateIcon>
            <icon-loading />
          </template>
        </btn>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Filter Search Product Grid (filter-search-product-grid)
 * -----------------------------------------------------------------------------
 * Component to display products in a grid and handling load more/load previous
 * pagination.
 *
 * @param {Object} pagination - Pagination data.
 * @param {Array} products - Array of formatted product card data.
 * @param {Number} totalProductCount - Total product count for the collection.
 *
 */

import Btn from '~global/btn/btn'
import ProductCard from '~global/product-card/product-card'
import IconLoading from '~icons/general/loading.svg'
import InGridBanner from '~async/in-grid-banner/in-grid-banner'

export default {
  name: 'FilterSearchProductGrid',

  components: {
    Btn,
    IconLoading,
    ProductCard,
    InGridBanner,
  },

  props: {
    activeFilters: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    mobileColumnCount: {
      type: Number,
      default: 1,
    },
    inGridBanner: {
      type: Object,
      default: null,
    },
    pagination: {
      type: Object,
      default: () => ({}),
    },
    products: {
      type: Array,
      default: () => [],
    },
    totalProductCount: {
      type: Number,
      default: 0,
    },
  },

  emits: {
    'load-more': () => true,
    'load-previous': () => true,
  },

  data() {
    return {
      itemRefs: [],
    }
  },

  computed: {

    /**
     * Compute 'Showing X of Y' message for pagination.
     */
    countMessage() {
      return this.$string('general.pagination.showing_count_of_total', {
        replace: {
          count: this.products.length,
          total: this.totalProductCount,
        },
      })
    },

    /**
     * Compute array of all items to display in the grid.
     * - Injects in grid content in with the products array.
     * @returns {Array} combined array of products and in grid content.
     */
    items() {
      const items = [...this.products]

      // Add logic for in grid content here

      return items
    },

    /**
     * Compute load more href.
     * @returns {String}
     */
    loadMoreLink() {
      const url = new URL(window.location.href)

      if (this.pagination.currentPage) {
        const pageNumber = this.pagination.currentPage + 1
        url.searchParams.set('page', pageNumber.toString())
      }

      return url.href
    },

    /**
     * Compute load previous href.
     * @returns {String}
     */
    loadPreviousLink() {
      const url = new URL(window.location.href)

      if (this.pagination.startPage) {
        const pageNumber = this.pagination.startPage - 1
        url.searchParams.set('page', pageNumber.toString())
      }

      return url.href
    },

    /**
     * Compute in-grid banner display
     * @returns {Boolean}
     */
    showInGridBanner() {
      if (!this.inGridBanner || this.activeFilters.length) {
        return false
      }

      return true
    },
  },

  beforeUpdate() {

    /**
     * Clear out the item refs array when the component updates.
     * - Avoids duplicate refs in the array.
     */
    this.itemRefs = []
  },

  methods: {

    /**
     * Set product card item refs.
     * @param {HTMLElement} element - Product card in v-for.
     */
    setItemRefs(element) {
      if (!element?.$el) {
        return
      }

      this.itemRefs.push(element.$el)
    },
  },
}
</script>

<style lang="scss">
@import './filter-search-product-grid';
</style>
